import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import './LoadingContext.scss';

const LoadingContext = createContext();

export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

export const LoadingProvider = ({ children, minSpinnerDelay = 0 }) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [isSplashVisible, setIsSplashVisible] = useState(true);
  const [showSpinner, setShowSpinner] = useState(false);
  const [spinnerStartTime, setSpinnerStartTime] = useState(null);

  const registerLoading = useCallback((componentId, isLoading) => {
    setLoadingStates(prev => ({
      ...prev,
      [componentId]: isLoading
    }));
  }, []);

  const unregisterLoading = useCallback((componentId) => {
    setLoadingStates(prev => {
      const newState = { ...prev };
      delete newState[componentId];
      return newState;
    });
  }, []);

  const isAnyLoading = Object.values(loadingStates).some(loading => loading === true);

  // Handle spinner delay logic
  useEffect(() => {
    if (isAnyLoading && !spinnerStartTime) {
      // Start the spinner timer
      setSpinnerStartTime(Date.now());
      setShowSpinner(true);
    } else if (!isAnyLoading && spinnerStartTime) {
      // Calculate elapsed time
      const elapsedTime = Date.now() - spinnerStartTime;
      const remainingTime = minSpinnerDelay - elapsedTime;

      if (remainingTime > 0) {
        // Show spinner for remaining time
        setTimeout(() => {
          setShowSpinner(false);
          setSpinnerStartTime(null);
        }, remainingTime);
      } else {
        // Hide spinner immediately
        setShowSpinner(false);
        setSpinnerStartTime(null);
      }
    } else if (!isAnyLoading && !spinnerStartTime) {
      // No loading and no timer, hide spinner
      setShowSpinner(false);
    }
  }, [isAnyLoading, minSpinnerDelay, spinnerStartTime]);

  const hideSplash = useCallback(() => {
    setIsSplashVisible(false);
  }, []);

  const showSplash = useCallback(() => {
    setIsSplashVisible(true);
  }, []);

  const value = {
    registerLoading,
    unregisterLoading,
    isAnyLoading,
    isSplashVisible,
    hideSplash,
    showSplash,
    loadingStates
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {showSpinner && (
        <div className="global-loading-spinner">
          <div className="spinner-overlay">
            <div className='loader'></div>
          </div>
        </div>
      )}
    </LoadingContext.Provider>
  );
};
